import React, { useState, useEffect } from 'react';
import { Flag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useReportUser, REPORT_REASONS, ReportReason } from '@/hooks/useReportUser';
import { cn } from '@/lib/utils';

interface ReportUserModalProps {
  userId: string;
  username?: string;
  variant?: 'button' | 'menuItem';
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const ReportUserModal: React.FC<ReportUserModalProps> = ({
  userId,
  username,
  variant = 'button',
  open: controlledOpen,
  onOpenChange
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState<ReportReason | null>(null);
  const [description, setDescription] = useState('');
  
  const { hasReported, isLoading, checkReportStatus, reportUser } = useReportUser(userId);

  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;
  const setIsOpen = isControlled ? onOpenChange! : setInternalOpen;

  useEffect(() => {
    checkReportStatus();
  }, [checkReportStatus]);

  const handleSubmit = async () => {
    if (!selectedReason) return;
    
    const success = await reportUser(selectedReason, description || undefined);
    if (success) {
      setIsOpen(false);
      setSelectedReason(null);
      setDescription('');
    }
  };

  const triggerElement = variant === 'menuItem' ? (
    <button
      className="flex items-center w-full px-2 py-1.5 text-sm text-white hover:bg-white/10 rounded-sm cursor-pointer"
      disabled={hasReported}
    >
      <Flag className="h-4 w-4 mr-2" />
      {hasReported ? 'Já denunciado' : 'Denunciar'}
    </button>
  ) : (
    <Button
      variant="outline"
      size="sm"
      disabled={hasReported}
      className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
    >
      <Flag className="h-4 w-4 mr-2" />
      {hasReported ? 'Denunciado' : 'Denunciar'}
    </Button>
  );

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {!isControlled && (
        <DialogTrigger asChild>
          {triggerElement}
        </DialogTrigger>
      )}
      <DialogContent className="bg-gray-900 border-white/10 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Flag className="h-5 w-5 text-amber-400" />
            Denunciar {username || 'usuário'}
          </DialogTitle>
          <DialogDescription className="text-white/60">
            Selecione o motivo da denúncia. Nossa equipe irá analisar e tomar as medidas necessárias.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-4">
          {REPORT_REASONS.map((reason) => (
            <button
              key={reason.value}
              onClick={() => setSelectedReason(reason.value)}
              className={cn(
                "w-full flex items-center gap-3 p-3 rounded-lg border transition-colors text-left",
                selectedReason === reason.value
                  ? "border-amber-500 bg-amber-500/10"
                  : "border-white/10 hover:border-white/20 bg-white/5"
              )}
            >
              <span className="text-xl">{reason.emoji}</span>
              <span className="text-white">{reason.label}</span>
            </button>
          ))}

          {selectedReason && (
            <div className="pt-2">
              <Textarea
                placeholder="Descreva o problema (opcional)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="bg-white/5 border-white/10 text-white placeholder:text-white/40 resize-none"
                rows={3}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setIsOpen(false)}
            className="bg-white/10 border-white/20 text-white hover:bg-white/20"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!selectedReason || isLoading}
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            {isLoading ? 'Enviando...' : 'Enviar denúncia'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
